import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {PasskeyService} from '../../_services';
import {describeThisDevice} from '../models/passkey.util';
import {POST_LOGIN_NEXT_URL_SESSION_KEY} from '../models/post-login';
import {environment} from '../../../environments/environment';

/**
 * JS-1396 follow-up — offered right after a recovery-code sign-in, which is
 * the precise signal that this device has no usable passkey (see
 * PasskeyController::loginRecovery()). Without this, the next new device
 * silently costs another recovery code until the user runs out and has to
 * fall back to the emailed reset.
 *
 * Reached via completePostLogin()'s overrideDestination from
 * passkey-recovery.component.ts, which means the session is ALREADY
 * WRITTEN by the time this screen runs - see post-login.ts's comment on why
 * that write cannot be deferred: enrol() below needs an authenticated
 * register-options call.
 *
 * Not the mandatory passkey-enrol screen and not a shared "optional mode" of
 * it: 2FA is already satisfied for this login (the user came in on a
 * recovery code, not with no second factor at all), so this screen has a
 * genuine, permanent "Not now" skip. passkey-enrol keeps exactly one
 * behaviour - no skip, ever (spec D9) - and stays untouched; this is a
 * separate route on purpose.
 */
@Component({
  moduleId: module.id,
  selector: 'app-passkey-add-device',
  templateUrl: `../views/passkey-add-device.component.html`,
  styleUrls: ['../styles/auth-style.scss'],
  providers: [MessageService]
})
export class PasskeyAddDeviceComponent implements OnInit {
  public logoUrl = environment.logoUrl;
  public enrolling = false;
  public recoveryCodesRemaining: number = null;

  // Resolution order, first match wins:
  //  1. router state {nextUrl} (post-login.ts's overrideDestination
  //     contract) - the normal case, no language-switch reload happened.
  //  2. sessionStorage[POST_LOGIN_NEXT_URL_SESSION_KEY] - completePostLogin()
  //     stashes it there before a language-switch reload, since that reload
  //     destroys router state entirely (browser-pass fix). Cleared on read
  //     so a later, unrelated login can never inherit a stale destination.
  //  3. /project - reached directly (a refresh, a pasted URL, or a stash
  //     that was never written because sessionStorage threw). Same fallback
  //     resolvePostLoginUrl() already uses. Never show a dead screen.
  private nextUrl = '/project';

  constructor(
    private router: Router,
    private passkeyService: PasskeyService,
    private messageService: MessageService,
    private translate: TranslateService
  ) {
    const navigation = this.router.getCurrentNavigation();
    const state = navigation && navigation.extras && navigation.extras.state;
    if (state && (state as any).nextUrl) {
      this.nextUrl = (state as any).nextUrl;
      return;
    }

    // sessionStorage can throw in some private-browsing modes - falls
    // through to the /project default above rather than showing a dead
    // screen.
    try {
      const stashed = sessionStorage.getItem(POST_LOGIN_NEXT_URL_SESSION_KEY);
      if (stashed) {
        sessionStorage.removeItem(POST_LOGIN_NEXT_URL_SESSION_KEY);
        this.nextUrl = stashed;
      }
    } catch (e) {
      // See comment above - keep the /project default.
    }
  }

  ngOnInit() {
    // The session already exists (see class comment), so this authenticated
    // call is safe here. The count comes from credentials() rather than
    // from the login-recovery response the user just consumed, because
    // post-login.ts's router-state contract is deliberately kept to just
    // {nextUrl} - growing it to also carry recovery_codes_remaining would
    // be a second copy of a number this screen can read fresh itself for
    // the cost of one GET on a screen the user is already sitting on.
    this.passkeyService.credentials().subscribe(response => {
      if (typeof response.recovery_codes_remaining === 'number' && response.recovery_codes_remaining <= 2) {
        this.recoveryCodesRemaining = response.recovery_codes_remaining;
      }
    });
  }

  async addPasskey() {
    if (this.enrolling) {
      return;
    }
    this.enrolling = true;

    try {
      // No enrolmentToken: the session already exists, so PasskeyService.
      // enrol() falls back to it via resolveUser() on the api side, exactly
      // like security-tab.component.ts's call.
      await this.passkeyService.enrol(describeThisDevice(navigator.userAgent));
      this.goToNext();
    } catch (error) {
      // Both failure modes land here identically - a local
      // PasskeyLocalCeremonyError (cancelled/timed-out system sheet) or an
      // HTTP failure - because either way the right move on THIS screen is
      // the same: toast and let them retry addPasskey() or bail with
      // notNow(). There is no token to burn and no mandate to enforce here.
      this.enrolling = false;
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('Error!'),
        detail: this.translate.instant('could not add passkey')
      });
    }
  }

  notNow() {
    this.goToNext();
  }

  private goToNext() {
    this.router.navigate([this.nextUrl]);
  }
}
